import React from 'react';
import { Profile } from '../../types/database.types';
import { 
  Target, 
  TrendingDown, 
  Zap, 
  Heart, 
  ShieldAlert, 
  Award, 
  Calendar, 
  Activity,
  Flame,
  Droplets,
  Scale,
  Sparkles,
  HeartPulse
} from 'lucide-react';
import { StreakDisciplineCard } from './AntiProcrastinationBanner';

interface RecompProgressCardProps {
  profile: Profile;
  daysRemaining: number;
  waterGoalReached: boolean;
  workoutDone: boolean;
  onNavigateToWorkout: () => void;
}

export const RecompProgressCard: React.FC<RecompProgressCardProps> = ({
  profile,
  daysRemaining,
  waterGoalReached,
  workoutDone,
  onNavigateToWorkout,
}) => {
  const currentWeight = profile.current_weight_kg;
  const targetWeight = profile.target_weight_kg;
  const totalToLose = 104.70 - targetWeight; // 15.0 kg
  const weightLost = 104.70 - currentWeight;
  const progressPercent = Math.min(100, Math.max(0, Math.round((weightLost / totalToLose) * 100)));

  return (
    <div className="space-y-4 max-w-xl mx-auto pb-32">
      {/* 1. Card de Disciplina Diária com Progressão de Cores (Verde -> Amarelo -> Vermelho -> Roxo) */}
      <StreakDisciplineCard
        streakDays={profile.streak_days}
        waterGoalReached={waterGoalReached}
        workoutDone={workoutDone}
        onNavigateToWorkout={onNavigateToWorkout}
      />

      {/* 2. CARD PROTOCOLO DURATESTON & HIDRATAÇÃO (Movido para cá conforme solicitado) */}
      <div className="liquid-glass rounded-[28px] p-5 border border-[#30D6FB]/30 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-40 h-40 bg-[#30D6FB]/10 rounded-full blur-3xl pointer-events-none" />

        <div className="flex items-start gap-3.5">
          <div className="p-3 rounded-[18px] bg-[#30D6FB]/15 text-[#30D6FB] border border-[#30D6FB]/30 shrink-0">
            <HeartPulse className="w-6 h-6 animate-pulse" />
          </div>

          <div className="flex-1">
            <div className="flex flex-wrap items-center justify-between gap-1">
              <span className="text-xs font-extrabold tracking-wider uppercase text-slate-900 dark:text-[#30D6FB]">
                Protocolo Durateston & Hidratação
              </span>
              <span className="text-[10px] tabular-nums font-bold px-2.5 py-0.5 rounded-full bg-[#30D6FB]/20 text-[#30D6FB] border border-[#30D6FB]/30">
                Laudo: 49.8% Água (Baixo)
              </span>
            </div>

            <p className="text-xs text-slate-600 dark:text-slate-300 mt-2 leading-relaxed font-medium">
              Esteroides anabolizantes aumentam a produção de hemácias (hematócrito) e a viscosidade do sangue. 
              <strong className="text-slate-900 dark:text-white"> 5.000ml diários de água</strong> são mandatórios para afinar o sangue, proteger os rins contra sobrecarga de metabólitos, prevenir pressão alta e acelerar a queima lipolítica.
            </p>
          </div>
        </div>
      </div>

      {/* 3. Cockpit Central da Recomposição: Trajetória 85kg em 60 Dias */}
      <div className="liquid-glass-elevated rounded-[28px] p-6 border border-[#BF5AF2]/30 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-[#BF5AF2]/15 via-pink-500/5 to-transparent rounded-full blur-3xl pointer-events-none" />

        {/* Top Tagline */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="p-2.5 rounded-[18px] bg-[#BF5AF2]/15 text-[#BF5AF2] border border-[#BF5AF2]/30">
              <Target className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-extrabold text-slate-900 dark:text-white">Recomposição Agressiva</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">Meta: -{totalToLose.toFixed(1)} kg em 60 Dias</p>
            </div>
          </div>

          <span className="px-3 py-1 rounded-full bg-[#BF5AF2]/15 text-[#BF5AF2] border border-[#BF5AF2]/30 text-xs tabular-nums font-extrabold">
            {daysRemaining}d RESTANTES
          </span>
        </div>

        {/* Display Duplo de Pesos */}
        <div className="grid grid-cols-2 gap-3 my-5">
          <div className="p-4 rounded-[22px] bg-white/70 dark:bg-black/60 border border-slate-200 dark:border-white/[0.08] text-center shadow-sm">
            <span className="text-[10px] uppercase font-bold tracking-widest text-slate-400">
              PESO ATUAL
            </span>
            <div className="text-4xl font-extrabold tabular-nums tracking-tight text-slate-900 dark:text-white mt-1">
              {currentWeight.toFixed(1)}
              <span className="text-sm font-normal text-slate-400 ml-1">kg</span>
            </div>
            <span className="text-[11px] tabular-nums font-bold text-[#FF453A] mt-0.5 inline-block">
              Gordura: 32.0% (33.5kg)
            </span>
          </div>

          <div className="p-4 rounded-[22px] bg-white/70 dark:bg-black/60 border border-[#BF5AF2]/40 text-center shadow-sm">
            <span className="text-[10px] uppercase font-bold tracking-widest text-[#BF5AF2]">
              META FINAL
            </span>
            <div className="text-4xl font-extrabold tabular-nums tracking-tight text-[#BF5AF2] mt-1">
              {targetWeight.toFixed(1)}
              <span className="text-sm font-normal text-[#BF5AF2]/60 ml-1">kg</span>
            </div>
            <span className="text-[11px] tabular-nums font-bold text-[#30D158] mt-0.5 inline-block">
              Gordura: &lt; 18.0%
            </span>
          </div>
        </div>

        {/* Barra de Progresso com Gradiente Apple */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs tabular-nums font-bold">
            <span className="text-slate-500 dark:text-slate-400">Trajetória dos {totalToLose.toFixed(1)}kg</span>
            <span className="text-[#BF5AF2]">{progressPercent}% Concluído</span>
          </div>
          <div className="w-full h-3.5 bg-slate-200 dark:bg-black/60 rounded-full overflow-hidden p-0.5 border border-slate-200 dark:border-white/[0.08]">
            <div
              className="h-full bg-gradient-to-r from-[#BF5AF2] via-[#FF375F] to-[#30D6FB] rounded-full shadow-[0_0_12px_rgba(191,90,242,0.5)] transition-all duration-700"
              style={{ width: `${Math.max(8, progressPercent)}%` }}
            />
          </div>
        </div>
      </div>

      {/* 4. Painel da Bioimpedância Oficial (Quadro Biométrico Detalhado) */}
      <div className="liquid-glass rounded-[28px] p-6 border border-slate-200 dark:border-white/[0.08] space-y-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-[#30D6FB]" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white">
              Laudo Oficial de Bioimpedância
            </h3>
          </div>
          <span className="text-[10px] tabular-nums font-bold px-2.5 py-0.5 rounded-full bg-[#30D6FB]/10 text-[#30D6FB] border border-[#30D6FB]/20">
            25 ANOS • 180 CM
          </span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
          {/* Massa Muscular */}
          <div className="p-3.5 rounded-[20px] bg-white/70 dark:bg-black/60 border border-slate-200 dark:border-white/[0.06] space-y-1 shadow-sm">
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Massa Muscular</span>
            <div className="text-xl font-extrabold tabular-nums text-[#30D158]">68,20 kg</div>
            <div className="text-[10px] text-slate-500 font-medium">65.1% (Excelente)</div>
          </div>

          {/* Metabolismo Basal */}
          <div className="p-3.5 rounded-[20px] bg-slate-50 dark:bg-black/60 border border-slate-200 dark:border-white/[0.06] space-y-1">
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Metabolismo Basal</span>
            <div className="text-xl font-extrabold tabular-nums text-[#FF9F0A]">2.098 kcal</div>
            <div className="text-[10px] text-slate-500 font-medium">TDEE ~3.100 kcal</div>
          </div>

          {/* Água Corporal */}
          <div className="p-3.5 rounded-[20px] bg-slate-50 dark:bg-black/60 border border-[#30D6FB]/30 space-y-1">
            <span className="text-[10px] font-semibold text-[#30D6FB] uppercase tracking-wider">Água Corporal</span>
            <div className="text-xl font-extrabold tabular-nums text-[#30D6FB]">49,8 %</div>
            <div className="text-[10px] text-[#FF453A] font-bold">Abaixo (Meta: &gt;55%)</div>
          </div>

          {/* Gordura Visceral */}
          <div className="p-3.5 rounded-[20px] bg-slate-50 dark:bg-black/60 border border-slate-200 dark:border-white/[0.06] space-y-1">
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Gordura Visceral</span>
            <div className="text-xl font-extrabold tabular-nums text-[#FF453A]">Nível 20,5</div>
            <div className="text-[10px] text-slate-500 font-medium">Faixa Obesa (Foco)</div>
          </div>

          {/* Massa Muscular Esquelética */}
          <div className="p-3.5 rounded-[20px] bg-slate-50 dark:bg-black/60 border border-slate-200 dark:border-white/[0.06] space-y-1">
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Músculo Esquelético</span>
            <div className="text-xl font-extrabold tabular-nums text-slate-900 dark:text-white">36,20 kg</div>
            <div className="text-[10px] text-slate-500 font-medium">34.6% Saudável</div>
          </div>

          {/* Suporte Hormonal */}
          <div className="p-3.5 rounded-[20px] bg-slate-50 dark:bg-black/60 border border-[#30D6FB]/20 space-y-1">
            <span className="text-[10px] font-semibold text-[#30D6FB] uppercase tracking-wider">Anabolismo</span>
            <div className="text-xl font-extrabold tabular-nums text-[#30D6FB]">Durateston</div>
            <div className="text-[10px] text-[#30D158] font-bold">Anti-catabolismo</div>
          </div>
        </div>
      </div>
    </div>
  );
};
